/**
 * Manage Document PiP window lifecycle for live sessions.
 */

import { useCallback, useEffect, useState } from "react";
import {
  isDocumentPictureInPictureSupported,
  openDocumentPictureInPicture,
  type DocumentPipWindow,
} from "./documentPictureInPicture";

export function useLiveSessionDocumentPip() {
  const [pipWindow, setPipWindow] = useState<DocumentPipWindow | null>(null);
  const supported = isDocumentPictureInPictureSupported();

  const closePip = useCallback(() => {
    try {
      pipWindow?.close();
    } catch {
      /* ignore */
    }
    setPipWindow(null);
  }, [pipWindow]);

  const openPip = useCallback(async () => {
    if (!supported) return null;
    try {
      const win = await openDocumentPictureInPicture({
        width: 420,
        height: 520,
      });
      if (!win) return null;
      setPipWindow(win);
      return win;
    } catch (err) {
      console.warn("Document PiP open failed", err);
      setPipWindow(null);
      return null;
    }
  }, [supported]);

  useEffect(() => {
    if (!pipWindow) return;
    const onHide = () => setPipWindow(null);
    pipWindow.addEventListener("pagehide", onHide);
    return () => {
      pipWindow.removeEventListener("pagehide", onHide);
    };
  }, [pipWindow]);

  useEffect(() => {
    return () => {
      try {
        pipWindow?.close();
      } catch {
        /* ignore */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    supported,
    pipWindow,
    pipOpen: Boolean(pipWindow),
    openPip,
    closePip,
  };
}
